import { Menu, Typography, type MenuProps, Space } from 'antd';
import { concat, isEqual } from 'lodash/fp';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    type ModelConfiguratorListDataFragment,
    ModelConfiguratorSortingField,
    type ModelConfiguratorFilteringRule,
    SortingOrder,
    useGetLabelsByFeatureModuleIdQuery,
    type LabelsDataFragment,
    useListModelConfiguratorsQuery,
    useGetBannerByModuleQuery,
    type BannerDataFragment,
    type ConfiguratorApplicationEntrypointContextDataFragment,
} from '../../../../api';
import { useThemeComponents } from '../../../../themes';
import renderMarkdown from '../../../../utilities/renderMarkdown';
import { useListCIReducer } from '../../../shared/CIPage/useListCIReducer';
import { ConfiguratorContainer } from '../ModelConfiguratorDetailsPage/ModelConfiguratorDetailsInnerPage';
import { usePersistConfiguratorJourneyValues } from '../usePersistConfiguratorJourneyValues';
import CarListContent from './CarListContent';
import { DropdownButtonContainer, DropdownLabelContainer, StyledDropdown, useFilterMenu } from './FilterMenu';
import HeaderImage from './HeaderImage';
import { Container, PageTitle, FilterContainer, FilterItem, StyledFilterButton } from './ui';

enum Sorting {
    PriceAsc = 'priceAsc',
    PriceDesc = 'priceDesc',
}

const Inner = ({ endpoint }: CarListingPagesProps) => {
    const { t } = useTranslation(['configuratorListVehicle']);
    const [shownModels, setShownModels] = useState<ModelConfiguratorListDataFragment[]>([]);
    const filterMenu = useFilterMenu();

    const { remove: removePersistedConfiguratorJourneyValue } = usePersistConfiguratorJourneyValues();

    useEffect(() => {
        removePersistedConfiguratorJourneyValue();
    }, [removePersistedConfiguratorJourneyValue]);

    const [state, dispatch] = useListCIReducer<ModelConfiguratorSortingField, ModelConfiguratorFilteringRule>({
        filter: {},
        sort: { field: ModelConfiguratorSortingField.VehiclePrice, order: SortingOrder.Asc },
        page: 1,
        pageSize: 21,
    });

    const { Button, ConfiguratorDropdown } = useThemeComponents();

    const { configuratorApplicationModule } = endpoint;
    const externalUrl = useMemo(
        () => configuratorApplicationModule.externalUrl,
        [configuratorApplicationModule.externalUrl]
    );

    const { data: labelData } = useGetLabelsByFeatureModuleIdQuery({
        fetchPolicy: 'cache-and-network',
        variables: {
            moduleId: configuratorApplicationModule.id,
        },
    });

    const label = useMemo(() => labelData?.label, [labelData?.label]) as LabelsDataFragment[];

    const { data: responseModels, loading } = useListModelConfiguratorsQuery({
        variables: {
            pagination: { offset: (state.page - 1) * state.pageSize, limit: state.pageSize },
            sort: state.sort,
            filter: {
                ...state.filter,
                moduleId: configuratorApplicationModule.id,
                vehicleEngines: filterMenu.filter.engines,
                vehicleBodyTypes: filterMenu.filter.bodyTypes,
                productionOnly: true,
                isActive: true,
            },
        },
    });

    const { data: bannerData, loading: bannerLoading } = useGetBannerByModuleQuery({
        variables: {
            moduleId: configuratorApplicationModule.id,
        },
        fetchPolicy: 'cache-and-network',
    });
    const banner = useMemo(() => bannerData?.banner, [bannerData?.banner]) as BannerDataFragment;

    // On scroll, assign visible models to shownModels
    useEffect(() => {
        if (responseModels) {
            if (!state.scroll) {
                setShownModels([]);
            }
            setShownModels(oldList => concat(oldList, responseModels.lists.items));
        }
    }, [responseModels, state.scroll]);

    // track latest company id
    const previousFilter = useRef(filterMenu.filter);

    useEffect(() => {
        if (!isEqual(previousFilter.current, filterMenu.filter)) {
            setShownModels(!loading && responseModels?.lists?.items ? responseModels.lists.items : []);
            previousFilter.current = filterMenu.filter;
            dispatch({ type: 'setPage', page: 1 });
        }
    }, [filterMenu.filter, dispatch, loading, responseModels]);

    const sortMenuOverlay = useMemo(() => {
        const onSelect: MenuProps['onSelect'] = ({ key }) => {
            switch (key) {
                case Sorting.PriceAsc:
                    dispatch({
                        type: 'setSort',
                        sortBy: {
                            field: ModelConfiguratorSortingField.VehiclePrice,
                            order: SortingOrder.Asc,
                        },
                    });
                    break;

                case Sorting.PriceDesc:
                    dispatch({
                        type: 'setSort',
                        sortBy: {
                            field: ModelConfiguratorSortingField.VehiclePrice,
                            order: SortingOrder.Desc,
                        },
                    });
                    break;

                default:
                    throw new Error('Invalid Sorting Rule');
            }
        };

        return (
            <Menu
                defaultSelectedKeys={[Sorting.PriceAsc]}
                items={[
                    {
                        label: t('configuratorListVehicle:sorts.priceAsc'),
                        key: Sorting.PriceAsc,
                    },
                    {
                        label: t('configuratorListVehicle:sorts.priceDesc'),
                        key: Sorting.PriceDesc,
                    },
                ]}
                onSelect={onSelect}
                style={{ maxHeight: 300, overflow: 'auto' }}
                selectable
            />
        );
    }, [dispatch, t]);

    const sortingLabel = useMemo(() => {
        switch (state.sort.field) {
            case ModelConfiguratorSortingField.VehiclePrice: {
                switch (state.sort.order) {
                    case SortingOrder.Asc:
                        return t('configuratorListVehicle:sorts.priceAsc');

                    case SortingOrder.Desc:
                        return t('configuratorListVehicle:sorts.priceDesc');

                    default:
                        throw new Error('Invalid sorting rule');
                }
            }

            default:
                throw new Error('Invalid sorting field');
        }
    }, [state.sort.field, state.sort.order, t]);

    // Scroll handlers
    const onNextPage = useCallback(() => {
        dispatch({ type: 'setPage', page: state.page + 1 });
    }, [dispatch, state.page]);

    const total = responseModels?.lists?.count || 0;
    const [hasLabel, setHasLabel] = useState<boolean>(false);

    return (
        <>
            {banner && banner.isActive && (
                <HeaderImage banner={banner} externalUrl={externalUrl} loading={bannerLoading} />
            )}
            <Container>
                <PageTitle>{renderMarkdown(t('configuratorListVehicle:title'))}</PageTitle>
                <FilterContainer>
                    <FilterItem>
                        <Typography.Text style={{ fontSize: 20 }}>
                            {t('configuratorListVehicle:labels.availability', { count: total })}
                        </Typography.Text>
                    </FilterItem>
                    <FilterItem right>
                        {filterMenu.render(endpoint.configuratorApplicationModule.id)}
                        <Space align="center" size={16}>
                            <Typography.Text style={{ fontSize: 18 }}>
                                {t('configuratorListVehicle:labels.sortBy')}
                            </Typography.Text>
                            <ConfiguratorDropdown overlay={sortMenuOverlay}>
                                <DropdownButtonContainer>
                                    <Button icon={null} type="tertiary" block>
                                        <StyledFilterButton>
                                            <DropdownLabelContainer>{sortingLabel}</DropdownLabelContainer>
                                            <StyledDropdown />
                                        </StyledFilterButton>
                                    </Button>
                                </DropdownButtonContainer>
                            </ConfiguratorDropdown>
                        </Space>
                    </FilterItem>
                </FilterContainer>
                <CarListContent
                    hasLabel={hasLabel}
                    label={label}
                    loading={loading}
                    onNextPage={onNextPage}
                    setHasLabel={setHasLabel}
                    shownModels={shownModels}
                    total={total}
                />
            </Container>
        </>
    );
};

type CarListingPagesProps = {
    endpoint: ConfiguratorApplicationEntrypointContextDataFragment;
};

const CarListingPage = ({ endpoint }: CarListingPagesProps) => {
    const { ConfiguratorLayout } = useThemeComponents();

    return (
        <ConfiguratorContainer>
            <ConfiguratorLayout>
                <Inner endpoint={endpoint} />
            </ConfiguratorLayout>
        </ConfiguratorContainer>
    );
};

export default CarListingPage;
